/**
 * src/pages/GlossaryAndReferences/sections/ReferencesSection.tsx
 * The curated reading library presenting primary and scholarly literature across:
 * - Levels: Introductory, Intermediate, Advanced
 * - Types: Primary Sources vs. Scholarly Interpretation
 * - Supported topic cross-links
 * - Honest bibliographic and verified external links
 */

import React, { useState, useMemo } from "react";
import { Link } from "@/components/common/Link";
import { REFERENCES, type ReferenceLevel, type ReferenceType, type ReferenceEntry } from "@/lib/knowledge/references";
import { BookMarked, ExternalLink, Layers, GraduationCap, Filter, Library } from "lucide-react";
import "../glossaryReferences.css";
export function ReferencesSection() {
  const [levelFilter, setLevelFilter] = useState<ReferenceLevel | "all">("all");
  const [typeFilter, setTypeFilter] = useState<ReferenceType | "all">("all");
  const filteredReferences = useMemo(() => {
    return REFERENCES.filter(ref => {
      const matchLevel = levelFilter === "all" || ref.level === levelFilter;
      const matchType = typeFilter === "all" || ref.type === typeFilter;
      return matchLevel && matchType;
    });
  }, [levelFilter, typeFilter]);
  return <section id="reading-library" className="w-full max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-border/40 min-w-0">
      {/* Section Header */}
      <div className="mb-8">
        <span className="ql-eyebrow">Academic Citations</span>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mt-2 mb-3">
          Foundational Literature & References
        </h2>
        <p className="font-sans text-sm sm:text-base text-muted-foreground max-w-3xl leading-relaxed">
          A three-tiered reading library categorizing primary historical treatises and peer-reviewed scholarly interpretations across epistemology, classical logic, and scientific demarcation.
        </p>
      </div>

      {/* Filter Controls Bar */}
      <div className="ql-glass p-4 sm:p-5 rounded-xl border border-border/60 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Tier / Level Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-muted-foreground uppercase flex items-center gap-1.5 mr-1">
            <GraduationCap className="size-3.5 text-primary" aria-hidden="true" />
            <span>Tier:</span>
          </span>
          {(["all", "introductory", "intermediate", "advanced"] as const).map(lvl => <button key={lvl} type="button" onClick={() => setLevelFilter(lvl)} aria-pressed={levelFilter === lvl} className={`gl-filter-pill ${levelFilter === lvl ? "gl-filter-pill-selected" : ""}`}>
              <span className="capitalize">{lvl === "all" ? "All Levels" : lvl}</span>
            </button>)}
        </div>

        {/* Source Type Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-muted-foreground uppercase flex items-center gap-1.5 mr-1">
            <Filter className="size-3.5 text-primary" aria-hidden="true" />
            <span>Type:</span>
          </span>
          {(["all", "primary", "secondary"] as const).map(t => <button key={t} type="button" onClick={() => setTypeFilter(t)} aria-pressed={typeFilter === t} className={`gl-filter-pill ${typeFilter === t ? "gl-filter-pill-selected" : ""}`}>
              <span>{t === "all" ? "All Types" : t === "primary" ? "Primary Source" : "Secondary Analysis"}</span>
            </button>)}
        </div>
      </div>

      {/* Grid of Reference Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 min-w-0">
        {filteredReferences.map(ref => {
        return <article key={ref.id} className="gl-ref-card flex flex-col justify-between min-w-0" aria-labelledby={`heading-ref-${ref.id}`}>
              <div>
                {/* Meta badges: Tier & Type */}
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="font-mono text-[11px] uppercase tracking-wider px-2 py-0.5 rounded bg-card/80 border border-border text-foreground">
                    {ref.level}
                  </span>
                  <span className={`font-mono text-[11px] uppercase tracking-wider px-2 py-0.5 rounded border ${ref.type === "primary" ? "bg-primary/10 border-primary/40 text-primary" : "bg-muted/40 border-border text-muted-foreground"}`}>
                    {ref.type === "primary" ? "Primary Source" : "Interpretation"}
                  </span>
                </div>

                {/* Reference Title & Author */}
                <h3 id={`heading-ref-${ref.id}`} className="font-serif text-base sm:text-lg font-bold text-foreground mb-1 leading-snug">
                  {ref.title}
                </h3>
                <p className="font-sans text-xs sm:text-sm font-semibold text-primary/90 mb-3">
                  {ref.author} <span className="font-mono text-muted-foreground font-normal">({ref.year}{ref.edition ? ` · ${ref.edition}` : ""})</span>
                </p>

                {/* Analytical Description */}
                <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4 break-words">
                  {ref.description}
                </p>
              </div>

              <div>
                {/* Topic Pages Supported */}
                <div className="pt-3 border-t border-border/40 mb-3">
                  <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider block mb-1.5">
                    Supports Topic:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {ref.pagesSupported.map((pg, idx) => <Link key={idx} to={pg.route} className="font-mono text-[11px] text-foreground hover:text-primary transition-colors underline underline-offset-2">
                        {pg.title}
                      </Link>)}
                  </div>
                </div>

                {/* Canonical External Link if verified */}
                {ref.url && <Link href={ref.url} newTab className="inline-flex items-center gap-1.5 text-xs font-mono text-primary hover:underline pt-1 min-h-[44px] break-all">
                    <span>Read Canonical Entry</span>
                    <ExternalLink className="size-3 shrink-0" aria-hidden="true" />
                  </Link>}
              </div>
            </article>;
      })}
      </div>
    </section>;
}